<template>
    <span>
        <teleport to="#teleports">
            <transition :name="transition" @after-leave="doDestroy">
                <div
                    v-show="!disabled && showPopper"
                    :id="tooltipId"
                    ref="dropdownRef"
                    class="el-popover a-popper"
                    :class="[
                        maxWidth === 'auto-width' ? 'a-tooltip__popper--auto-width' : '',
                        popperClass,
                        visibleArrow ? 'a-popper--with-arrow' : ''
                    ]"
                    :style="computedStyle"
                    role="tooltip"
                    :aria-hidden="disabled || !showPopper ? true : false"
                    tabindex="0"
                    @keydown.esc="handleEsc"
                    @keydown.tab="handleKeydown"
                >
                    <div
                        v-if="visibleArrow"
                        ref="arrowEl"
                        class="a-popper__arrow"
                        data-popper-arrow
                    />
                    <div v-if="loading && !$slots.default" class="el-popover__loading" />
                    <div ref="resizableContainer" class="el-popover__wrap">
                        <slot />
                        <div v-if="$slots.actions" class="el-alert__actions mx-16">
                            <slot name="actions" />
                        </div>
                    </div>
                </div>
            </transition>
        </teleport>

        <div ref="triggerRef" class="el-popover__reference">
            <slot name="reference" />
        </div>
    </span>
</template>

<script setup>
import ResizeObserver from 'resize-observer-polyfill';
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from 'vue';
import { usePopper } from '@core/composables/usePopper';
import GlobalPubSub from '@utils/global-pub-sub.ts';

const props = defineProps({
    trigger: {
        type: String,
        default: 'click',
        validator: val => ['click', 'focus', 'hover', 'manual'].includes(val),
    },
    placement: { type: String, default: 'top' },
    openDelay: { type: Number, default: 0 },
    disabled: Boolean,
    manual: Boolean,
    popperClass: { type: String, default: '' },
    maxWidth: { type: [String, Number], default: 'default' },
    visibleArrow: { type: Boolean, default: true },
    transition: { type: String, default: '' },
});

const emit = defineEmits(['show', 'hide']);

const showPopper = ref(false);
const triggerRef = ref(null);
const dropdownRef = ref(null);
const resizableContainer = ref(null);
const tooltipId = `el-popover-${Math.floor(Math.random() * 10000)}`;
const timer = ref(null);
const arrowEl = ref(null);
let observer = null;

const computedStyle = computed(() => {
    return typeof props.maxWidth === 'number'
        ? { maxWidth: `${props.maxWidth}px` }
        : {};
});

const popperOptions = computed(() => ({
    placement: props.placement,
    modifiers: [
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'arrow', options: { element: arrowEl.value?.$el } },
        { name: 'preventOverflow', options: { boundary: 'viewport' } },
        { name: 'flip', options: { fallbackPlacements: ['top', 'right', 'left', 'bottom'] } },
    ],
    ...props.popperOptions,
}));

const { initializePopper, updatePopper, destroyPopper } = usePopper(triggerRef, dropdownRef, popperOptions.value);

function doShow() {
    showPopper.value = true;
    emit('show');
}

function doClose() {
    showPopper.value = false;
    emit('hide');
}

function doDestroy() {
    destroyPopper();
}

function doToggle() {
    showPopper.value = !showPopper.value;
}

function handleMouseEnter() {
    clearTimeout(timer.value);
    timer.value = setTimeout(doShow, props.openDelay);
}

function handleMouseLeave() {
    clearTimeout(timer.value);
    timer.value = setTimeout(doClose, 200);
}

function handleDocumentClick(e) {
    const refEl = triggerRef.value;
    const popEl = dropdownRef.value;
    if (!refEl || !popEl || refEl.contains(e.target) || popEl.contains(e.target)) return;
    showPopper.value = false;
}

function handleEsc(e) {
    if (e.key === 'Escape' && props.trigger !== 'manual') doClose();
}

function handleKeydown() {
    dropdownRef.value?.focus();
}

function setupListeners() {
    const refEl = triggerRef.value;
    const popEl = dropdownRef.value;
    if (!refEl || !popEl) return;

    refEl.setAttribute('aria-describedby', tooltipId);
    refEl.setAttribute('tabindex', '0');
    popEl.setAttribute('tabindex', '0');

    if (props.trigger === 'click') {
        refEl.addEventListener('click', e => {
            if (!showPopper.value) GlobalPubSub.emit('hideAllPopovers');
            doToggle();
            e.stopPropagation();
        });
        document.addEventListener('click', handleDocumentClick, true);
    } else if (props.trigger === 'hover') {
        refEl.addEventListener('mouseenter', handleMouseEnter);
        refEl.addEventListener('mouseleave', handleMouseLeave);
        popEl.addEventListener('mouseenter', handleMouseEnter);
        popEl.addEventListener('mouseleave', handleMouseLeave);
    } else if (props.trigger === 'focus') {
        if (refEl.querySelector('input, textarea')) {
            refEl.addEventListener('focusin', doShow);
            refEl.addEventListener('focusout', doClose);
        } else {
            refEl.addEventListener('mousedown', doShow);
            refEl.addEventListener('mouseup', doClose);
        }
    }

    refEl.addEventListener('keydown', handleKeydown);
}

onMounted(() => {
    nextTick(() => {
        setupListeners();
        if (props.trigger === 'manual' && showPopper.value) {
            nextTick(async () => {
                await initializePopper();
            });
        }
        observer = new ResizeObserver(() => updatePopper());
        if (resizableContainer.value) observer.observe(resizableContainer.value);
    });

    GlobalPubSub.on('hideAllPopovers', doClose);
});

onBeforeUnmount(() => {
    destroyPopper();
    GlobalPubSub.off('hideAllPopovers', doClose);
    document.removeEventListener('click', handleDocumentClick, true);
    observer?.disconnect();
});

watch(showPopper, visible => {
    if (visible) {
        nextTick(async () => {
            await initializePopper();
            requestAnimationFrame(() => {
                updatePopper();
            });
        });
    }
});
</script>

<style scoped lang="postcss">
.el-popover {
    @mixin caption;
    box-shadow: 0 0 0 1px var(--av-brand-primary), var(--av-shadow-regular);
    background: var(--av-inversed-primary);
    color: var(--av-fixed-primary);
    max-width: 325px;
}
</style>
